import { useState } from 'react';
import { FaTimes, FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import { duplicateBoard } from '@/services/kanbanBoardService';
import '@/styles/ActionItemsStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * DuplicateBoardModal Component
 * Modal for duplicating an existing Kanban board
 * @param {Object} props Component props
 * @param {Object} props.board - The board to duplicate
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onSuccess - Callback when board is duplicated successfully
 */
const DuplicateBoardModal = ({ board, onClose, onSuccess }) => {
    const { apiFetch } = useAuth();
    const [boardName, setBoardName] = useState(`${board.name} (Copy)`);
    const [isPublic, setIsPublic] = useState(false);
    const [copyAccessGrants, setCopyAccessGrants] = useState(false);
    const [duplicating, setDuplicating] = useState(false);

    const handleDuplicate = async () => {
        if (!boardName.trim()) {
            toast.error('Board name is required');
            return;
        }

        setDuplicating(true);
        try {
            const result = await duplicateBoard(apiFetch, board.boardId, {
                name: boardName,
                isPublic: isPublic,
                copyAccessGrants: copyAccessGrants
            });
            
            toast.success('Board duplicated successfully');
            onClose();
            
            if (onSuccess) {
                onSuccess(result);
            } else {
                // Refresh the page to show the new board
                window.location.reload();
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to duplicate board');
        } finally {
            setDuplicating(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><FaCopy /> Duplicate Board</h2>
                    <button className="close-button" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="info-box">
                        <p>
                            This will create a new board with the same columns and settings as <strong>{board.name}</strong>.
                            Action items will not be copied.
                        </p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="boardName">New Board Name *</label>
                        <input
                            id="boardName"
                            type="text"
                            value={boardName}
                            onChange={(e) => setBoardName(e.target.value)}
                            placeholder="Enter name for duplicated board"
                            autoFocus
                            maxLength={200}
                        />
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={isPublic}
                                onChange={(e) => setIsPublic(e.target.checked)}
                            />
                            <span>Make this board public</span>
                        </label>
                        <small className="form-hint">Public boards are visible to all users</small>
                    </div>

                    {board.isOwner && (
                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={copyAccessGrants}
                                    onChange={(e) => setCopyAccessGrants(e.target.checked)}
                                />
                                <span>Copy access permissions</span>
                            </label>
                            <small className="form-hint">
                                Users with access to the original board will be granted the same access to the new board
                            </small>
                        </div>
                    )}

                    <div className="duplication-summary">
                        <h4>What will be copied:</h4>
                        <ul>
                            <li>✅ Board columns and their settings</li>
                            <li>✅ Column colors, icons, and WIP limits</li>
                            <li>✅ Board color and icon</li>
                            {copyAccessGrants && <li>✅ User access permissions</li>}
                            <li>❌ Action items (not copied)</li>
                            <li>❌ Board activity history (not copied)</li>
                        </ul>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose} disabled={duplicating}>
                        Cancel
                    </button>
                    <button className="btn-primary" onClick={handleDuplicate} disabled={duplicating}>
                        {duplicating ? 'Duplicating...' : 'Duplicate Board'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DuplicateBoardModal;
